import React from 'react';
import { Navigate } from 'react-router-dom';
import { useRorosStore } from '@/stores/rorosStore';
import { useRorosLaunchpadStatus } from '@/hooks/roros/launchpad';
import { useRorosSocket } from '@/hooks/useRorosSocket';
import { useSocket } from '@/hooks/useSocket';
import { Spinner, Button } from '@/components/ui';
import type { RorosOperatingMode } from '@/types/roros/enums';
import { ApiError } from '@/services/roros/base-roros.service';
import { NoOrganizationAccess } from '@/components/organization/NoOrganizationAccess';

/**
 * F2.0 — Launchpad Route Guard
 * Wraps ROROS routes. If onboarding is not complete, redirects to /roros/launchpad.
 * Checks once per session (caches in rorosStore).
 */
export function RorosGuard({ children }: { children: React.ReactNode }) {
  // v1.1.14 fix — open the socket.io connection. useRorosSocket only
  // SUBSCRIBES; useSocket() is what actually opens the connection.
  // BaseLayout also calls useSocket() (added in v1.1.13), but for the
  // user's specific session-stack BaseLayout's hook didn't fire reliably.
  // Calling it here on RorosGuard guarantees the connection opens for
  // every ROROS page; useSocket() is ref-counted and dedupes the
  // underlying socket instance so the duplicate call is safe.
  useSocket();
  // Wire up ROROS WebSocket event listeners for real-time updates
  useRorosSocket();

  const onboardingComplete = useRorosStore((s) => s.onboardingComplete);
  const setOnboardingComplete = useRorosStore((s) => s.setOnboardingComplete);
  const setOperatingMode = useRorosStore((s) => s.setOperatingMode);

  // Only fetch if not already cached as complete
  const { data, isLoading, isError, error } = useRorosLaunchpadStatus();

  const hasChecked = React.useRef(false);

  React.useEffect(() => {
    if (data && !hasChecked.current) {
      hasChecked.current = true;
      const complete = data.isComplete ?? data.is_complete ?? false;
      setOnboardingComplete(complete);
      // Try to extract operating mode from step data
      if (complete) {
        const modeStep = data.steps.find((s) => s.step === 'operating_mode');
        if (modeStep?.data?.value) {
          setOperatingMode(modeStep.data.value as RorosOperatingMode);
        } else if (modeStep?.data?.operating_mode) {
          setOperatingMode(modeStep.data.operating_mode as RorosOperatingMode);
        }
      }
    }
  }, [data, setOnboardingComplete, setOperatingMode]);

  // If already cached as complete, render immediately
  if (onboardingComplete) {
    return <>{children}</>;
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" label="Loading..." />
      </div>
    );
  }

  // API error — distinguish org-context/permission errors from genuine network/5xx errors.
  // ApiError (from base-roros.service) carries a .status field. A 400/403 means the user
  // has no valid org context — the ProtectedApp guard should have prevented reaching here,
  // but as a defensive fallback we show NoOrganizationAccess instead of the confusing
  // connection-error copy. Any other error (network failure, 5xx) shows the "check your
  // connection" message with a Retry button.
  if (isError) {
    const isOrgContextError =
      error instanceof ApiError && (error.status === 400 || error.status === 403);

    if (isOrgContextError) {
      return <NoOrganizationAccess />;
    }

    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-destructive font-medium">Failed to verify onboarding status</p>
        <p className="text-sm text-muted-foreground">Please check your connection and try again.</p>
        <Button onClick={() => window.location.reload()}>
          Retry
        </Button>
      </div>
    );
  }

  // Check the API response
  if (data?.isComplete || data?.is_complete) {
    return <>{children}</>;
  }

  // Onboarding not complete — redirect to launchpad
  return <Navigate to="/roros/launchpad" replace />;
}
